import { useCallback, useMemo } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { type SportMonetizationCurrentEvent } from '../../../dto/monetization-events.types';
import { SportEventType } from '../../../dto/sport.types';
import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';
import { Loader } from '../../Loader/Loader';
import { Timer } from '../../Timer/Timer';
import classes from '../MonetizationEventsDialog/MonetizationEvents.module.scss';

import sportClasses from './SportsWidget.module.scss';

export const SportsWidget = observer(({ event }: { event: SportMonetizationCurrentEvent }) => {
  const { t } = useTranslation();
  const { monetizationEventsStore, sportEventsStore } = useStores();
  const { widgetImageUrl, metadata } = event;
  const { homeScore, awayScore, status, sideAImageUrl, sideBImageUrl, startDate } = metadata ?? {};
  const isSportsWidgetEnabled = useFlag('crown_sports_feature', false);

  const determineSubType = (gameNameType: string) => {
    switch (gameNameType) {
      case SportEventType.GAME_OF_THE_DAY:
        return SportEventType.GAME_OF_THE_DAY;
      case SportEventType.TOTO:
        return SportEventType.TOTO;
      default:
        return SportEventType.GAME_OF_THE_DAY;
    }
  };

  const analyticsParams = useMemo(
    () =>
      ({
        event_name: 'widget',
        feature_type: 'crown_sports',
        feature_sub_value: 'crown_sports',
        click_location: 'widget',
        monetization_event_id: event.eventId,
        feature_sub_type: determineSubType(event.gameNameType),
      }) as const,
    [event.gameNameType, event.eventId],
  );

  const onClickHandler = () => {
    void sportEventsStore.startSportEvent();

    void monetizationEventsStore.sendAnalyticsEvent({ ...analyticsParams, event_action: 'click' });
  };

  const matchStatus = useMemo(() => {
    if (!status) return null;
    return {
      isStarted: status === 'STARTED',
      isUpcoming: status === 'UPCOMING',
      isEnded: status === 'ENDED',
    };
  }, [status]);

  const timerDate = useMemo(() => {
    if (!startDate || new Date(startDate) < new Date()) {
      return null;
    } else if (matchStatus?.isUpcoming && startDate) {
      return new Date(startDate);
    }
    return new Date(event.endDate);
  }, [matchStatus?.isUpcoming, startDate, event.endDate]);

  const onTimerEnd = useCallback(() => {
    void monetizationEventsStore.fetch();
  }, [monetizationEventsStore]);

  const isLoading = sportEventsStore.isLoadingGame;

  const isToto = event.gameNameType === SportEventType.TOTO;

  if (!isSportsWidgetEnabled) {
    return null;
  }

  return (
    <div className="home-widget" data-testid="sports-widget">
      <div className={classNames(classes.monetizationEventWidget, 'monetizationEventWidget', event.gameNameType)}>
        <div className={classNames(classes.widget, 'circle-parent')} onClick={onClickHandler}>
          <div className="circle" />
          <img alt="Widget" className={classNames(classes.icon, sportClasses.icon)} src={widgetImageUrl} />
          {!matchStatus?.isEnded && timerDate && (
            <Timer className={classNames(classes.timer, 'widget-timer')} endDate={timerDate} onTimerEnd={onTimerEnd} />
          )}
          {matchStatus?.isStarted && !isToto && (
            <div className={sportClasses.matchStatus}>
              <div className={sportClasses.timer}>
                <span className={sportClasses.liveIndicator}></span>
                <span className={sportClasses.timerText}>{t('sports.live')}</span>
              </div>
              <div className={sportClasses.scoreContainer}>
                <img alt="Home Team" className={sportClasses.teamImage} src={sideAImageUrl} />
                <span className={sportClasses.scoreText}>
                  {homeScore ?? 0}-{awayScore ?? 0}
                </span>
                <img alt="Away Team" className={sportClasses.teamImage} src={sideBImageUrl} />
              </div>
            </div>
          )}
        </div>
        {isLoading && <Loader cover fullScreen />}
      </div>
    </div>
  );
});
